import * as React from 'react';
import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

type BaseTextFieldProps = Omit<MuiTextFieldProps, 'onChange' | 'error'>;

export interface InputProps extends BaseTextFieldProps {
  /** react-toolbox: callback with just the value string */
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  /** react-toolbox: error message string (also sets error state) */
  error?: string;
  /** react-toolbox: floating label text */
  label?: string;
  /** react-toolbox: hint text (maps to placeholder) */
  hint?: string;
  /** react-toolbox: icon to display */
  icon?: React.ReactNode;
  /** react-toolbox: whether the input is multiline */
  multiline?: boolean;
  /** react-toolbox: number of rows for multiline */
  rows?: number;
  /** react-toolbox: max length of input */
  maxLength?: number;
  /** react-toolbox: inner ref for accessing the input element */
  innerRef?: React.Ref<HTMLInputElement>;
}

/**
 * Input wrapper - maps react-toolbox Input to MUI TextField
 * - `onChange(value)` transformed from MUI's `onChange(event)`
 * - `error` string maps to helperText and error prop
 * - `hint` maps to placeholder
 * - `icon` maps to InputAdornment
 * - `innerRef` maps to inputRef
 * - `children` renders below the input (not as endAdornment)
 * - Default `fullWidth` to match react-toolbox behavior
 */
const TextField = React.forwardRef<HTMLDivElement, InputProps>(
  (
    {
      onChange,
      error,
      hint,
      icon,
      innerRef,
      maxLength,
      children,
      slotProps,
      sx,
      fullWidth = true,
      variant = 'standard',
      ...rest
    },
    ref,
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event.target.value, event);
      }
    };

    // Build slotProps with icon support
    const inputSlotProps: MuiTextFieldProps['slotProps'] = {
      ...slotProps,
    };

    if (icon) {
      inputSlotProps.input = {
        ...slotProps?.input,
        startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
      };
    }

    if (maxLength) {
      inputSlotProps.htmlInput = {
        ...slotProps?.htmlInput,
        maxLength,
      };
    }

    return (
      <div>
        <MuiTextField
          ref={ref}
          inputRef={innerRef}
          onChange={handleChange}
          error={Boolean(error)}
          helperText={error || undefined}
          placeholder={hint}
          slotProps={inputSlotProps}
          variant={variant}
          fullWidth={fullWidth}
          sx={{
            marginBottom: children ? '4px' : '16px',
            ...sx,
          }}
          {...rest}
        />
        {children && <div style={{ marginBottom: '16px' }}>{children}</div>}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
